import React, { useMemo } from 'react'
import { Box, Flex, Icon, Text, Loading } from '../../atoms'

import { useTheme } from 'styled-components'
import { SavedListingsGrid } from './SavedListingsGrid'
import {
  ListingsContainer,
  ListingsStatsSection,
  ListingsMetricCard,
  ListingsMetricValue,
  ListingsMetricLabel,
  ListingsEmptyState,
  ListingsEmptyStateIcon,
  ListingsEmptyStateText,
} from './SavedListings.styles'
import type { Listing, SupportedLanguage } from '@popety-io/types'

export interface SavedListingsTranslations {
  loading?: string
  emptyTitle?: string
  emptyDescription?: string
  totalSaved?: string
  listings?: string
  averagePrice?: string
  perListing?: string
  avgPricePerSqm?: string
  livingArea?: string
  avgLivingArea?: string
  priceRange?: string
  to?: string
  avgRooms?: string
  perListing2?: string
  sqm?: string
  // Grid card translations
  rooms?: string
  built?: string
  listed?: string
  active?: string
  expired?: string
  removeFromSaved?: string
  perSqm?: string
  propertyImage?: string
  propertyListing?: string
  addressNotAvailable?: string
  priceOnRequest?: string
  today?: string
  yesterday?: string
  daysAgo?: string
  weeksAgo?: string
  monthsAgo?: string
  yearsAgo?: string
}

const defaultTranslations: SavedListingsTranslations = {
  loading: 'Loading saved listings...',
  emptyTitle: 'No saved listings yet',
  emptyDescription:
    'Save listings from the "Listings around the plot" tab to compare them later',
  totalSaved: 'Total Saved',
  listings: 'Listings',
  averagePrice: 'Average Price',
  perListing: 'Per listing',
  avgPricePerSqm: 'Avg Price/m²',
  livingArea: 'Living area',
  avgLivingArea: 'Avg Living Area',
  priceRange: 'Price Range',
  to: 'to',
  avgRooms: 'Avg Rooms',
  perListing2: 'Per listing',
  sqm: 'm²',
}

const localizedFallbacks: Record<
  SupportedLanguage,
  Partial<SavedListingsTranslations>
> = {
  en: {
    sqm: 'm²',
    rooms: 'rooms',
    built: 'built',
    listed: 'Listed',
    today: 'Today',
    yesterday: 'Yesterday',
    daysAgo: 'days ago',
    weeksAgo: 'weeks ago',
    monthsAgo: 'months ago',
    yearsAgo: 'years ago',
    priceOnRequest: 'Price on request',
    addressNotAvailable: 'Address not available',
  },
  fr: {
    sqm: 'm²',
    rooms: 'pièces',
    built: 'construit',
    listed: 'Publié',
    today: "Aujourd'hui",
    yesterday: 'Hier',
    daysAgo: 'jours',
    weeksAgo: 'semaines',
    monthsAgo: 'mois',
    yearsAgo: 'ans',
    priceOnRequest: 'Prix sur demande',
    addressNotAvailable: 'Adresse non disponible',
  },
  de: {
    sqm: 'm²',
    rooms: 'Zimmer',
    built: 'gebaut',
    listed: 'Inseriert',
    today: 'Heute',
    yesterday: 'Gestern',
    daysAgo: 'Tage',
    weeksAgo: 'Wochen',
    monthsAgo: 'Monate',
    yearsAgo: 'Jahre',
    priceOnRequest: 'Preis auf Anfrage',
    addressNotAvailable: 'Adresse nicht verfügbar',
  },
  it: {
    sqm: 'm²',
    rooms: 'stanze',
    built: 'costruito',
    listed: 'Pubblicato',
    today: 'Oggi',
    yesterday: 'Ieri',
    daysAgo: 'giorni fa',
    weeksAgo: 'settimane fa',
    monthsAgo: 'mesi fa',
    yearsAgo: 'anni fa',
    priceOnRequest: 'Prezzo su richiesta',
    addressNotAvailable: 'Indirizzo non disponibile',
  },
}

const rawKeyByField: Partial<Record<keyof SavedListingsTranslations, string>> =
  {
    sqm: 'sqm',
    rooms: 'rooms',
    built: 'built',
    listed: 'listed',
    today: 'today',
    yesterday: 'yesterday',
    daysAgo: 'daysAgo',
    weeksAgo: 'weeksAgo',
    monthsAgo: 'monthsAgo',
    yearsAgo: 'yearsAgo',
    priceOnRequest: 'priceOnRequest',
    addressNotAvailable: 'addressNotAvailable',
  }

const normalizeTranslations = (
  translations: SavedListingsTranslations,
  language: SupportedLanguage = 'en'
) => {
  const normalized = { ...translations }
  const fallbacks = localizedFallbacks[language]

  Object.entries(rawKeyByField).forEach(([field, rawKey]) => {
    const key = field as keyof SavedListingsTranslations
    const value = normalized[key]

    if (
      typeof value !== 'string' ||
      value === rawKey ||
      value.toLowerCase() === rawKey.toLowerCase()
    ) {
      normalized[key] = fallbacks[key]
    }
  })

  return normalized
}

interface SavedListingsProps {
  listings: Listing[]
  isLoading?: boolean
  onListingClick: (listing: Listing) => void
  onRemoveListing?: (listingId: number) => void
  translations?: SavedListingsTranslations
  language?: SupportedLanguage
}

export const SavedListings: React.FC<SavedListingsProps> = ({
  listings,
  isLoading,
  onListingClick,
  onRemoveListing,
  translations: propTranslations,
  language,
}) => {
  const translations = normalizeTranslations(
    { ...defaultTranslations, ...propTranslations },
    language
  )
  const theme = useTheme()

  // Calculate statistics for saved listings
  const stats = useMemo(() => {
    if (!Array.isArray(listings) || listings.length === 0) {
      return {
        totalSaved: 0,
        avgPrice: 0,
        avgPricePerM2: 0,
        avgLivingArea: 0,
        priceRange: { min: 0, max: 0 },
        avgRooms: 0,
      }
    }

    const prices = listings.map((l) => l.price).filter((p) => p > 0)
    const livingAreas = listings.map((l) => l.area).filter((a) => a > 0)
    const rooms = listings
      .map((l) => l.rooms_nb)
      .filter((r): r is number => r !== undefined && r !== null && r > 0)

    const avgPrice =
      prices.length > 0
        ? prices.reduce((sum, p) => sum + p, 0) / prices.length
        : 0

    const avgLivingArea =
      livingAreas.length > 0
        ? livingAreas.reduce((sum, a) => sum + a, 0) / livingAreas.length
        : 0

    const pricesPerM2 = listings
      .map(
        (l) =>
          l.price_per_square_meter || (l.price && l.area ? l.price / l.area : 0)
      )
      .filter((p) => p > 0)

    const avgPricePerM2 =
      pricesPerM2.length > 0
        ? pricesPerM2.reduce((sum, p) => sum + p, 0) / pricesPerM2.length
        : 0

    const sortedPrices = [...prices].sort((a, b) => a - b)

    return {
      totalSaved: listings.length,
      avgPrice,
      avgPricePerM2,
      avgLivingArea,
      priceRange: {
        min: sortedPrices[0] || 0,
        max: sortedPrices[sortedPrices.length - 1] || 0,
      },
      avgRooms:
        rooms.length > 0
          ? rooms.reduce((sum, r) => sum + r, 0) / rooms.length
          : 0,
    }
  }, [listings])

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('de-CH').format(Math.round(value))
  }

  if (isLoading) {
    return (
      <ListingsContainer>
        <Box p="48px" textAlign="center">
          <Loading />
          <Box mt="16px">
            <Text color={theme.colors.text.secondary}>
              {translations.loading}
            </Text>
          </Box>
        </Box>
      </ListingsContainer>
    )
  }

  if (!Array.isArray(listings) || listings?.length === 0) {
    return (
      <ListingsContainer>
        <ListingsEmptyState>
          <ListingsEmptyStateIcon>
            <Icon
              name="bookmark"
              size="large"
              color={theme.colors.text.tertiary}
            />
          </ListingsEmptyStateIcon>
          <ListingsEmptyStateText>
            <Box mb="8px">
              <Text variant="h5" weight="semibold">
                {translations.emptyTitle}
              </Text>
            </Box>
            <Text color={theme.colors.text.secondary}>
              {translations.emptyDescription}
            </Text>
          </ListingsEmptyStateText>
        </ListingsEmptyState>
      </ListingsContainer>
    )
  }

  return (
    <ListingsContainer>
      {/* Statistics Section */}
      <ListingsStatsSection>
        <Flex gap="16px" wrap="wrap">
          <ListingsMetricCard>
            <ListingsMetricLabel>
              <Icon name="bookmark" size="small" />
              {translations.totalSaved}
            </ListingsMetricLabel>
            <ListingsMetricValue>{stats.totalSaved}</ListingsMetricValue>
            <Text color={theme.colors.text.tertiary}>
              {translations.listings}
            </Text>
          </ListingsMetricCard>

          <ListingsMetricCard $accent>
            <ListingsMetricLabel>
              <Icon name="trending-up" size="small" />
              {translations.averagePrice}
            </ListingsMetricLabel>
            <ListingsMetricValue color={theme.colors.primary}>
              {formatCurrency(stats.avgPrice)}
            </ListingsMetricValue>
            <Text color={theme.colors.text.tertiary}>
              {translations.perListing}
            </Text>
          </ListingsMetricCard>

          <ListingsMetricCard>
            <ListingsMetricLabel>
              <Icon name="square" size="small" />
              {translations.avgPricePerSqm}
            </ListingsMetricLabel>
            <ListingsMetricValue>
              {formatCurrency(stats.avgPricePerM2)}
            </ListingsMetricValue>
            <Text color={theme.colors.text.tertiary}>
              {translations.livingArea}
            </Text>
          </ListingsMetricCard>

          <ListingsMetricCard>
            <ListingsMetricLabel>
              <Icon name="maximize" size="small" />
              {translations.avgLivingArea}
            </ListingsMetricLabel>
            <ListingsMetricValue>
              {formatNumber(stats.avgLivingArea)} {translations.sqm}
            </ListingsMetricValue>
            <Text color={theme.colors.text.tertiary}>
              {translations.perListing}
            </Text>
          </ListingsMetricCard>

          <ListingsMetricCard>
            <ListingsMetricLabel>
              <Icon name="bar-chart-2" size="small" />
              {translations.priceRange}
            </ListingsMetricLabel>
            <ListingsMetricValue>
              {formatCurrency(stats.priceRange.min)}
            </ListingsMetricValue>
            <Text color={theme.colors.text.tertiary}>
              {translations.to} {formatCurrency(stats.priceRange.max)}
            </Text>
          </ListingsMetricCard>

          <ListingsMetricCard>
            <ListingsMetricLabel>
              <Icon name="home" size="small" />
              {translations.avgRooms}
            </ListingsMetricLabel>
            <ListingsMetricValue>
              {stats.avgRooms > 0 ? stats.avgRooms.toFixed(1) : '-'}
            </ListingsMetricValue>
            <Text color={theme.colors.text.tertiary}>
              {translations.perListing2}
            </Text>
          </ListingsMetricCard>
        </Flex>
      </ListingsStatsSection>

      {/* Grid of saved listings cards - filter out undefined to preserve grid defaults */}
      <SavedListingsGrid
        listings={listings}
        onListingClick={onListingClick}
        onRemoveListing={onRemoveListing}
        language={language}
        translations={Object.fromEntries(
          Object.entries({
            rooms: translations.rooms,
            sqm: translations.sqm,
            built: translations.built,
            listed: translations.listed,
            active: translations.active,
            expired: translations.expired,
            removeFromSaved: translations.removeFromSaved,
            perSqm: translations.perSqm,
            propertyImage: translations.propertyImage,
            propertyListing: translations.propertyListing,
            addressNotAvailable: translations.addressNotAvailable,
            priceOnRequest: translations.priceOnRequest,
            today: translations.today,
            yesterday: translations.yesterday,
            daysAgo: translations.daysAgo,
            weeksAgo: translations.weeksAgo,
            monthsAgo: translations.monthsAgo,
            yearsAgo: translations.yearsAgo,
          }).filter(([, v]) => v !== undefined)
        )}
      />
    </ListingsContainer>
  )
}
